import React, { useState, useEffect } from 'react';
import { X, Building2, Mail, Phone, MapPin, Globe, Calendar, RefreshCw } from 'lucide-react';
import { superAdminAPI } from '../../services/api';

const TenantModal = ({ isOpen, onClose, tenant, mode, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    domain: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    description: '',
    subscriptionType: 'BASIC',
    status: 'ACTIVE'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (tenant && (mode === 'edit' || mode === 'view')) {
        setFormData({
          name: tenant.name || '',
          code: tenant.code || '',
          domain: tenant.domain || '',
          contactEmail: tenant.contactEmail || '',
          contactPhone: tenant.contactPhone || '',
          address: tenant.address || '',
          description: tenant.description || '',
          subscriptionType: tenant.subscriptionType || 'BASIC',
          status: tenant.status || 'ACTIVE'
        });
      } else if (mode === 'add') {
        // Reset form for add mode only when modal opens
        setFormData({
          name: '',
          code: '',
          domain: '',
          contactEmail: '',
          contactPhone: '',
          address: '',
          description: '',
          subscriptionType: 'BASIC',
          status: 'ACTIVE'
        });
      }
      setErrors({});
    }
  }, [tenant, mode, isOpen]);





  // Function to generate tenant code from name
  const generateTenantCode = (name) => {
    if (!name) return '';

    return name
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, '') // Remove special characters except spaces
      .split(/\s+/) // Split by whitespace
      .filter(word => word.length > 0) // Remove empty strings
      .map(word => {
        // Take first 3 characters of each word, or the whole word if shorter
        return word.length <= 3 ? word : word.substring(0, 3);
      })
      .join('_') // Join with underscores
      .substring(0, 20); // Limit to 20 characters
  };

  // Force generate tenant code manually
  const handleForceGenerateCode = () => {
    const generatedCode = generateTenantCode(formData.name);
    setFormData(prev => ({
      ...prev,
      code: generatedCode
    }));

    // Clear any existing code error
    if (errors.code) {
      setErrors(prev => ({
        ...prev,
        code: ''
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Convert tenant code to uppercase automatically
    const processedValue = name === 'code' ? value.toUpperCase() : value;

    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: processedValue
      };

      // Auto-generate tenant code from name if name is being changed
      if (name === 'name' && mode === 'add') {
        const generatedCode = generateTenantCode(value);
        console.log('Auto-generating tenant code:', {
          inputName: value,
          generatedCode,
          currentCode: prev.code,
          previousName: prev.name,
          previousGeneratedCode: prev.name ? generateTenantCode(prev.name) : '',
          isEmpty: !prev.code,
          matchesPrevious: prev.code === generateTenantCode(prev.name || '')
        });

        // Auto-populate if:
        // 1. Code field is empty, OR
        // 2. Code matches what would be generated from the previous name (user hasn't manually edited)
        const shouldUpdate = !prev.code || prev.code === generateTenantCode(prev.name || '');

        if (shouldUpdate) {
          console.log('Updating tenant code to:', generatedCode);
          newData.code = generatedCode;
        } else {
          console.log('Not updating tenant code - user has manually edited it');
        }
      }

      return newData;
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Tenant name is required';
    if (!formData.code.trim()) newErrors.code = 'Tenant code is required';
    if (!formData.domain.trim()) newErrors.domain = 'Domain is required';
    if (!formData.contactEmail.trim()) newErrors.contactEmail = 'Contact email is required';

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.contactEmail && !emailRegex.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid email address';
    }

    // Validate tenant code format (uppercase letters, numbers, underscores only)
    const codeRegex = /^[A-Z0-9_]+$/;
    if (formData.code && !codeRegex.test(formData.code)) {
      console.log('Tenant code validation failed:', {
        code: formData.code,
        codeLength: formData.code.length,
        regexTest: codeRegex.test(formData.code),
        codeCharCodes: formData.code.split('').map(char => ({ char, charCode: char.charCodeAt(0) }))
      });
      newErrors.code = 'Tenant code must contain only uppercase letters, numbers, and underscores';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('Form submission started with data:', formData);

    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    console.log('Form validation passed, submitting to backend');
    setLoading(true);
    try {
      if (mode === 'add') {
        console.log('Creating tenant with data:', formData);
        await superAdminAPI.createTenant(formData);
      } else if (mode === 'edit') {
        console.log('Updating tenant with data:', formData);
        await superAdminAPI.updateTenant(tenant.id, formData);
      }

      onSave && onSave();
      onClose();
    } catch (error) {
      console.error('Error saving tenant:', error);
      console.error('Error response:', error.response?.data);
      // Handle specific error messages from backend
      if (error.response?.data?.message) {
        setErrors({ general: error.response.data.message });
      } else {
        setErrors({ general: 'Failed to save tenant. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isViewMode = mode === 'view';
  const title = mode === 'add' ? 'Add New Tenant' : mode === 'edit' ? 'Edit Tenant' : 'View Tenant';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto modal-responsive">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <Building2 className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-600 text-sm">{errors.general}</p>
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tenant Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={isViewMode}
                className={`input-field ${errors.name ? 'border-red-300' : ''}`}
                placeholder="e.g., ABC University"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tenant Code *
                {mode === 'add' && (
                  <span className="text-xs text-gray-500 font-normal ml-2">
                    (Auto-generated from name, editable)
                  </span>
                )}
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className={`input-field flex-1 ${errors.code ? 'border-red-300' : ''}`}
                  placeholder="e.g., ABC_COL"
                  style={{ textTransform: 'uppercase' }}
                />
                {!isViewMode && (
                  <button
                    type="button"
                    onClick={handleForceGenerateCode}
                    disabled={!formData.name.trim()}
                    className="btn-secondary flex items-center px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Force generate code from tenant name"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                )}
              </div>
              {errors.code && <p className="text-red-500 text-xs mt-1">{errors.code}</p>}
              {mode === 'add' && !errors.code && (
                <p className="text-gray-500 text-xs mt-1">
                  Uppercase letters, numbers, and underscores only. Auto-generated from tenant name but can be customized.
                  <br />
                  <span className="text-blue-600">Click the refresh button to manually regenerate code from current name.</span>
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Domain *
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  name="domain"
                  value={formData.domain}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className={`input-field pl-10 ${errors.domain ? 'border-red-300' : ''}`}
                  placeholder="e.g., abc.edu"
                />
              </div>
              {errors.domain && <p className="text-red-500 text-xs mt-1">{errors.domain}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Email *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className={`input-field pl-10 ${errors.contactEmail ? 'border-red-300' : ''}`}
                  placeholder="admin@abc.edu"
                />
              </div>
              {errors.contactEmail && <p className="text-red-500 text-xs mt-1">{errors.contactEmail}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Phone
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="tel"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="input-field pl-10"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subscription Type
              </label>
              <select
                name="subscriptionType"
                value={formData.subscriptionType}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="input-field"
              >
                <option value="BASIC">Basic</option>
                <option value="PREMIUM">Premium</option>
                <option value="ENTERPRISE">Enterprise</option>
              </select>
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                disabled={isViewMode}
                rows={3}
                className="input-field pl-10"
                placeholder="Enter full address..."
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              disabled={isViewMode}
              rows={3}
              className="input-field"
              placeholder="Brief description of the institution..."
            />
          </div>

          {/* Status (only for edit mode) */}
          {mode === 'edit' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="input-field"
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="SUSPENDED">Suspended</option>
                <option value="PENDING">Pending</option>
              </select>
            </div>
          )}

          {/* View mode additional info */}
          {isViewMode && tenant && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Created At
                </label>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Calendar className="h-4 w-4" />
                  <span>{new Date(tenant.createdAt).toLocaleString()}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Users
                </label>
                <span className="text-sm text-gray-900">{tenant.currentUsers || 0}</span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {isViewMode ? 'Close' : 'Cancel'}
            </button>
            {!isViewMode && (
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Saving...' : mode === 'add' ? 'Create Tenant' : 'Update Tenant'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default TenantModal;
